<!-- Alert.vue (final) -->
<template>
  <div
    class="fixed top-0 left-1/2 z-99999999 w-[min(440px,calc(100vw-1.5rem))] -translate-x-1/2 pt-[env(safe-area-inset-top)] pointer-events-none"
    aria-live="polite"
    aria-atomic="true"
  >
    <div class="mt-20 px-3">
      <transition
        mode="out-in"
        enter-active-class="transition ease-out duration-200"
        enter-from-class="opacity-0 -translate-y-2"
        enter-to-class="opacity-100 translate-y-0"
        leave-active-class="transition ease-in duration-150"
        leave-from-class="opacity-100 translate-y-0"
        leave-to-class="opacity-0 -translate-y-2"
        @after-leave="onAfterLeave"
      >
        <div
          v-if="visible"
          :key="toastKey"
          class="pointer-events-auto relative overflow-hidden rounded-xl border shadow-xl ring-1 ring-black/5 backdrop-blur"
          :class="toastClasses"
          role="alert"
          @mouseenter="pauseAutoClose"
          @mouseleave="resumeAutoClose"
        >
          <!-- Close button (top-right) -->
          <button
            type="button"
            class="absolute right-2 top-2 z-10 inline-flex h-7 w-7 items-center justify-center rounded-lg opacity-70 hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-offset-2"
            :class="closeRingClass"
            aria-label="Close"
            @click="close"
          >
            <i class="fa-solid fa-xmark text-sm" aria-hidden="true" />
          </button>

          <!-- Content -->
          <div class="flex items-center gap-3 px-4 py-3 pr-10">
            <!-- Icon -->
            <div class="shrink-0 flex h-6 w-6 items-center justify-center">
              <i class="fa-solid text-lg leading-none" :class="iconClass" aria-hidden="true" />
            </div>

            <!-- Message -->
            <div class="min-w-0 flex-1">
              <div class="text-sm font-semibold leading-snug wrap-break-word text-center sm:text-left">
                {{ localMsg }}
              </div>
            </div>
          </div>

          <!-- Progress bar -->
          <div class="h-1 w-full bg-black/5">
            <div
              v-if="shouldShowProgress"
              class="toast-progress h-full w-full"
              :class="progressClass"
              :style="progressStyle"
            />
            <div v-else class="h-full w-full" :class="progressClass" style="opacity: 0.35" />
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, nextTick } from 'vue';
import { getModule } from 'vuex-module-decorators';
import GeneralModule from '@/store/modules/General';

type AlertColor = 'danger' | 'error' | 'success' | 'warning' | 'info' | 'primary' | 'secondary' | '';

type PendingToast = {
  id: string;
  msg: string;
  color: AlertColor;
} | null;

export default defineComponent({
  name: 'Alert',

  data() {
    return {
      visible: false,
      localMsg: '',
      localColor: '' as AlertColor,
      localId: '',
      closingId: '',
      seq: 0,
      timerId: null as ReturnType<typeof setTimeout> | null,
      isClosing: false,
      isLeaving: false,
      endAt: 0,
      remainingMs: 0,
      isPaused: false,

      pendingToast: null as PendingToast
    };
  },

  computed: {
    GeneralInstance() {
      // @ts-ignore
      return getModule(GeneralModule, this.$store);
    },

    storeIsAlert(): boolean {
      return Boolean(this.GeneralInstance?.alert?.isAlert);
    },

    storeMsg(): string {
      return String(this.GeneralInstance?.alert?.alertMsg || '');
    },

    storeColor(): AlertColor {
      return (this.GeneralInstance?.alert?.alertColor || '') as AlertColor;
    },

    storeId(): string {
      return String(this.GeneralInstance?.alert?.id || '');
    },

    toastKey(): string {
      return `${this.seq}:${this.localId}:${this.localColor}:${this.localMsg}`;
    },

    shouldShowProgress(): boolean {
      return true;
    },

    shouldAutoClose(): boolean {
      return true;
    },

    durationMs(): number {
      switch (this.localColor) {
        case 'success':
          return 3500;
        case 'info':
        case 'primary':
          return 4500;
        case 'warning':
        case 'secondary':
          return 6000;
        case 'danger':
        case 'error':
          return 6000;
        default:
          return 4500;
      }
    },

    toastClasses(): string {
      switch (this.localColor) {
        case 'danger':
        case 'error':
          return 'bg-red-50/90 border-red-200 text-red-900';
        case 'success':
          return 'bg-emerald-50/90 border-emerald-200 text-emerald-900';
        case 'warning':
          return 'bg-amber-50/90 border-amber-200 text-amber-950';
        case 'info':
          return 'bg-sky-50/90 border-sky-200 text-sky-900';
        case 'primary':
          return 'bg-primary/10 border-primary/30 text-primary';
        case 'secondary':
          return 'bg-secondary/10 border-secondary/30 text-secondary';
        default:
          return 'bg-slate-50/90 border-slate-200 text-slate-900';
      }
    },

    iconClass(): string {
      switch (this.localColor) {
        case 'danger':
        case 'error':
          return 'fa-circle-xmark';
        case 'success':
          return 'fa-circle-check';
        case 'warning':
          return 'fa-triangle-exclamation';
        default:
          return 'fa-circle-info';
      }
    },

    progressClass(): string {
      switch (this.localColor) {
        case 'danger':
        case 'error':
          return 'bg-red-500';
        case 'success':
          return 'bg-emerald-500';
        case 'warning':
          return 'bg-amber-500';
        case 'info':
          return 'bg-sky-500';
        case 'primary':
          return 'bg-primary';
        case 'secondary':
          return 'bg-secondary';
        default:
          return 'bg-slate-500';
      }
    },

    closeRingClass(): string {
      switch (this.localColor) {
        case 'danger':
        case 'error':
          return 'focus:ring-red-400 focus:ring-offset-red-50';
        case 'success':
          return 'focus:ring-emerald-400 focus:ring-offset-emerald-50';
        case 'warning':
          return 'focus:ring-amber-400 focus:ring-offset-amber-50';
        case 'info':
          return 'focus:ring-sky-400 focus:ring-offset-sky-50';
        case 'primary':
          return 'focus:ring-primary/50 focus:ring-offset-transparent';
        case 'secondary':
          return 'focus:ring-secondary/50 focus:ring-offset-transparent';
        default:
          return 'focus:ring-slate-400 focus:ring-offset-slate-50';
      }
    },

    progressStyle(): Record<string, string> {
      const d = Math.max(0, this.durationMs);
      return {
        transformOrigin: 'left center',
        animationDuration: `${d}ms`,
        animationPlayState: this.isPaused ? 'paused' : 'running'
      };
    }
  },

  watch: {
    storeId: {
      immediate: true,
      async handler() {
        this.clearTimer();
        if (!this.storeIsAlert || !this.storeId) {
          this.pendingToast = null;
          this.visible = false;
          this.isLeaving = false;
          this.isClosing = false;
          this.isPaused = false;
          this.remainingMs = 0;
          this.endAt = 0;
          return;
        }

        const incoming = {
          id: this.storeId,
          msg: this.storeMsg,
          color: this.storeColor
        } as const;

        // if we're in leave animation, queue latest only
        if (this.isLeaving) {
          this.pendingToast = incoming;
          return;
        }

        // If currently visible -> queue latest, close current first
        if (this.visible) {
          this.pendingToast = incoming;

          this.isClosing = true;
          this.isLeaving = true;
          this.closingId = this.localId;

          this.visible = false;
          await nextTick();
          return;
        }

        this.showToast(incoming);
      }
    }
  },

  beforeUnmount() {
    this.clearTimer();
  },

  methods: {
    clearTimer() {
      if (this.timerId) {
        clearTimeout(this.timerId);
        this.timerId = null;
      }
    },

    startTimer(ms: number) {
      this.clearTimer();

      const dur = Math.max(0, ms);
      if (!this.shouldAutoClose || dur <= 0) return;

      // token to prevent stale close
      const keyAtSchedule = this.toastKey;

      this.remainingMs = dur;
      this.endAt = performance.now() + dur;

      this.timerId = setTimeout(() => {
        this.timerId = null;

        // Only close if still showing the same toast
        if (!this.visible) return;
        if (this.isClosing) return;
        if (this.toastKey !== keyAtSchedule) return;

        // force close
        this.isClosing = true;
        this.isLeaving = true;
        this.closingId = this.localId;
        this.visible = false;
      }, dur);
    },

    showToast(payload: { id: string; msg: string; color: AlertColor }) {
      this.localId = payload.id;
      this.localMsg = payload.msg;
      this.localColor = payload.color;

      this.seq += 1;
      this.visible = true;

      this.isClosing = false;
      this.isLeaving = false;
      this.closingId = '';

      this.isPaused = false;

      this.startTimer(this.durationMs);
    },

    pauseAutoClose() {
      // return;

      if (!this.shouldAutoClose) return;
      if (this.isPaused) return;
      if (!this.visible) return;
      if (this.localColor === 'success') return;

      const now = performance.now();
      const remain = Math.max(0, this.endAt - now);

      this.remainingMs = remain;
      this.isPaused = true;
      this.clearTimer();
    },

    resumeAutoClose() {
      // return;

      if (!this.shouldAutoClose) return;
      if (!this.isPaused) return;
      if (!this.visible) return;

      if (this.localColor === 'success') return;

      this.isPaused = false;
      this.startTimer(this.remainingMs);
    },

    onAfterLeave() {
      this.clearTimer();

      this.isPaused = false;
      this.isClosing = false;
      this.isLeaving = false;
      this.remainingMs = 0;
      this.endAt = 0;

      const gi: any = this.GeneralInstance;
      const currentId = String(gi?.alert?.id || '');

      if (currentId && this.closingId && currentId === this.closingId) {
        gi?.CloseAlert?.();
      }

      this.closingId = '';

      if (this.pendingToast) {
        const next = this.pendingToast;
        this.pendingToast = null;

        const latestStoreId = String(this.GeneralInstance?.alert?.id || '');
        const latestIsAlert = Boolean(this.GeneralInstance?.alert?.isAlert);

        if (latestIsAlert && latestStoreId === next.id) {
          this.showToast(next);
        }
      }
    },

    close() {
      this.clearTimer();

      this.isPaused = false;
      this.isClosing = true;
      this.isLeaving = true;

      this.closingId = this.localId;
      this.pendingToast = null;

      this.visible = false;
    }
  }
});
</script>

<style scoped>
.toast-progress {
  will-change: transform;
  animation-name: toastShrink;
  animation-timing-function: linear;
  animation-fill-mode: forwards;
}

@keyframes toastShrink {
  from {
    transform: scaleX(1);
  }
  to {
    transform: scaleX(0);
  }
}
</style>
